import type { PropertyTypeSchema } from '../hooks/usePropertySchema';

export type BookingQuestionType =
  | 'short_text'
  | 'long_text'
  | 'single_select'
  | 'multi_select'
  | 'phone'
  | 'checkbox';

export interface BookingQuestion {
  key: string;
  label: string;
  type: BookingQuestionType;
  required: boolean;
  options: string[];
  map_to_property?: string;
}

export interface BookingAnswer {
  key: string;
  label: string;
  type: string;
  answer: unknown;
}

export const BOOKING_QUESTION_MAX = {
  QUESTIONS: 10,
  LABEL: 255,
  OPTIONS: 20,
  OPTION_LEN: 100,
  KEY_LEN: 32,
} as const;

export const KEY_RE = /^[a-z0-9_]+$/;

export const SELECT_QUESTION_TYPES: readonly BookingQuestionType[] = [
  'single_select',
  'multi_select',
];

export function isSelectType(t: BookingQuestionType): boolean {
  return SELECT_QUESTION_TYPES.includes(t);
}

export const ANSWERS_SNAPSHOT_PROPERTY_TYPE_NAMES: ReadonlySet<string> =
  new Set([
    'ahoy_custom_answers', // ahoy_booking
    'ahoy_booking_answers', // ahoy_meeting
  ]);

export function isAnswersSnapshotProperty(
  propertyTypeName: string | undefined | null
): boolean {
  return (
    typeof propertyTypeName === 'string' &&
    ANSWERS_SNAPSHOT_PROPERTY_TYPE_NAMES.has(propertyTypeName)
  );
}

export const MAPPABLE_QUESTION_TYPES: readonly BookingQuestionType[] = [
  'short_text',
  'long_text',
  'phone',
  'single_select',
];

export function isMappableType(t: BookingQuestionType): boolean {
  return MAPPABLE_QUESTION_TYPES.includes(t);
}

const _QUESTION_TYPE_SET: ReadonlySet<string> = new Set([
  'short_text',
  'long_text',
  'single_select',
  'multi_select',
  'phone',
  'checkbox',
]);

export const QUESTION_TYPE_OPTIONS: ReadonlyArray<{
  id: BookingQuestionType;
  label: string;
}> = [
  { id: 'short_text', label: 'Short text' },
  { id: 'long_text', label: 'Long text' },
  { id: 'single_select', label: 'Single select' },
  { id: 'multi_select', label: 'Multi select' },
  { id: 'phone', label: 'Phone' },
  { id: 'checkbox', label: 'Checkbox' },
];

function _isQuestionType(value: unknown): value is BookingQuestionType {
  return typeof value === 'string' && _QUESTION_TYPE_SET.has(value);
}

/**
 * Coerce a stored booking-questions / answers property value into an array of
 * raw rows. The value arrives in one of two shapes depending on the backend
 * field type it flowed through:
 *   - a JSON-encoded STRING — the `booking_questions` field type
 *     (`ahoy_custom_questions`) is a StringValueType, and event payloads may
 *     carry the raw string.
 *   - a native ARRAY — the `json` field type (`ahoy_custom_answers`) is
 *     returned already-parsed by the CRM read API.
 * Anything else yields [] so callers stay defensive.
 */
function toRawRows(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string' && value.length > 0) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
    return Array.isArray(parsed) ? parsed : [];
  }
  return [];
}

export function parseBookingQuestions(value: unknown): BookingQuestion[] {
  const result: BookingQuestion[] = [];
  const seenKeys = new Set<string>();

  for (const item of toRawRows(value)) {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) {
      continue;
    }

    const record = item as Record<string, unknown>;

    if (!_isQuestionType(record['type'])) {
      continue;
    }

    const key = typeof record['key'] === 'string' ? record['key'] : '';
    if (!key || seenKeys.has(key)) {
      continue;
    }

    const label = typeof record['label'] === 'string' ? record['label'] : '';
    const required =
      record['required'] === true || record['required'] === 'true';
    const options = Array.isArray(record['options'])
      ? record['options'].filter(
          (opt): opt is string => typeof opt === 'string'
        )
      : [];

    const mapToProperty =
      typeof record['map_to_property'] === 'string' &&
      record['map_to_property'].length > 0 &&
      isMappableType(record['type'])
        ? { map_to_property: record['map_to_property'] as string }
        : {};

    seenKeys.add(key);
    result.push({
      key,
      label,
      type: record['type'],
      required,
      options,
      ...mapToProperty,
    });
  }

  return result;
}

export function serializeBookingQuestions(qs: BookingQuestion[]): string {
  return JSON.stringify(
    qs.map(({ key, label, type, required, options, map_to_property }) => {
      const obj: Record<string, unknown> = {
        key,
        label,
        type,
        required,
        options,
      };
      if (
        isMappableType(type) &&
        typeof map_to_property === 'string' &&
        map_to_property.length > 0
      ) {
        obj['map_to_property'] = map_to_property;
      }
      return obj;
    })
  );
}

export function generateQuestionKey(
  existingKeys?: ReadonlySet<string> | string[]
): string {
  const existing =
    existingKeys instanceof Set ? existingKeys : new Set(existingKeys ?? []);

  let key = '';
  for (let attempt = 0; attempt < 20; attempt++) {
    const bytes = crypto.getRandomValues(new Uint8Array(4));
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join(
      ''
    );
    key = `q_${hex}`;
    if (!existing.has(key)) {
      return key;
    }
  }

  return key;
}

export function validateBookingQuestions(qs: BookingQuestion[]): string | null {
  if (qs.length > BOOKING_QUESTION_MAX.QUESTIONS) {
    return `A maximum of ${BOOKING_QUESTION_MAX.QUESTIONS} questions is allowed`;
  }

  const seenKeys = new Set<string>();

  for (const q of qs) {
    if (seenKeys.has(q.key)) {
      return `Duplicate question key "${q.key}"`;
    }
    seenKeys.add(q.key);

    const trimmedLabel = q.label.trim();
    if (
      trimmedLabel.length === 0 ||
      trimmedLabel.length > BOOKING_QUESTION_MAX.LABEL
    ) {
      return `Question label must be between 1 and ${BOOKING_QUESTION_MAX.LABEL} characters`;
    }

    if (q.map_to_property !== undefined) {
      if (!isMappableType(q.type)) {
        return `Question "${trimmedLabel}" cannot map its answer to a contact property`;
      }
      if (q.map_to_property.length > BOOKING_QUESTION_MAX.LABEL) {
        return `Property mapping must be at most ${BOOKING_QUESTION_MAX.LABEL} characters`;
      }
    }

    if (isSelectType(q.type)) {
      if (q.options.length === 0) {
        return `Question "${trimmedLabel}" must have at least one option`;
      }
      if (q.options.length > BOOKING_QUESTION_MAX.OPTIONS) {
        return `Question "${trimmedLabel}" can have at most ${BOOKING_QUESTION_MAX.OPTIONS} options`;
      }

      const seenOptions = new Set<string>();
      for (const opt of q.options) {
        const trimmedOpt = opt.trim();
        if (
          trimmedOpt.length === 0 ||
          trimmedOpt.length > BOOKING_QUESTION_MAX.OPTION_LEN
        ) {
          return `Options must be between 1 and ${BOOKING_QUESTION_MAX.OPTION_LEN} characters`;
        }
        if (seenOptions.has(trimmedOpt)) {
          return `Duplicate option "${trimmedOpt}" in question "${trimmedLabel}"`;
        }
        seenOptions.add(trimmedOpt);
      }
    } else if (q.options.length > 0) {
      return `Question "${trimmedLabel}" must not have options for its type`;
    }
  }

  return null;
}

export function parseBookingAnswers(value: unknown): BookingAnswer[] {
  const result: BookingAnswer[] = [];

  for (const item of toRawRows(value)) {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) {
      continue;
    }

    const record = item as Record<string, unknown>;
    const key = typeof record['key'] === 'string' ? record['key'] : '';
    if (!key) {
      continue;
    }

    // A missing, non-string, or blank label falls back to the key, so every
    // surface (overview rows, timeline sub-items, list cells, accessible
    // text) names the question the same way.
    const rawLabel = record['label'];
    const label =
      typeof rawLabel === 'string' && rawLabel.trim() ? rawLabel : key;
    const type = typeof record['type'] === 'string' ? record['type'] : '';

    result.push({ key, label, type, answer: record['answer'] });
  }

  return result;
}

export function formatAnswer(answer: unknown, type: string): string {
  if (answer === null || answer === undefined || answer === '') {
    return '';
  }

  if (type === 'multi_select') {
    return Array.isArray(answer) ? answer.join(', ') : String(answer);
  }

  if (type === 'checkbox') {
    return answer === true || answer === 'true' ? 'Yes' : 'No';
  }

  return String(answer);
}

/**
 * Build a compact, readable "Label: answer; …" string from an already-parsed
 * BookingAnswer array. Renders "—" for missing answers. Returns "" when there
 * are no answers, so callers can apply their own empty placeholder.
 */
export function formatBookingAnswers(answers: BookingAnswer[]): string {
  return answers
    .map(
      answer =>
        `${answer.label}: ${formatAnswer(answer.answer, answer.type) || '—'}`
    )
    .join('; ');
}

/**
 * Booking question type → the CRM field type whose read renderer (and type
 * icon) best represents the answer. Unknown / legacy types fall back to text.
 */
const QUESTION_TYPE_FIELD_TYPES: Readonly<Record<BookingQuestionType, string>> =
  {
    short_text: 'text',
    long_text: 'textarea',
    phone: 'phone_number',
    single_select: 'select',
    multi_select: 'select',
    checkbox: 'boolean',
  };

function toOptionList(
  answer: unknown
): Array<{ value: string; label: string }> {
  const values = Array.isArray(answer) ? answer : [answer];
  return values
    .filter(
      (v): v is string | number | boolean =>
        typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
    )
    .map(v => ({ value: String(v), label: String(v) }));
}

/**
 * Describe one answers-snapshot row as a synthetic `PropertyTypeSchema`, so
 * the canonical property renderers (`PropertyValueLabel`, `PropertyDiffValue`,
 * `getPropertyTypeIconComponent`) can draw it exactly like a native property:
 * the type icon, Yes/No for checkboxes, chips for multi-selects, the long-text
 * clamp. This is the same trick `JsonPropertyFields` uses for JSON-with-schema
 * properties — the snapshot row is self-describing, so no companion schema is
 * needed.
 *
 * Select answers are surfaced as their own options so option-label resolution
 * passes the stored text through unchanged; a checkbox gets the boolean
 * defaults (Yes / No) from `getEffectiveOptions`.
 */
export function buildBookingAnswerPropertyType(
  answer: BookingAnswer
): PropertyTypeSchema {
  const fieldTypeName = _isQuestionType(answer.type)
    ? QUESTION_TYPE_FIELD_TYPES[answer.type]
    : 'text';
  const isMulti = answer.type === 'multi_select';
  return {
    property_type_name: `__booking_answer_${answer.key}`,
    entity_type_name: 'ahoy_booking',
    label: answer.label,
    field_type_name: fieldTypeName,
    description: '',
    is_required: false,
    is_unique_value: false,
    is_multi: isMulti,
    value_write_access: 'none',
    value_read_access: 'all',
    value_update_access: 'none',
    ...(fieldTypeName === 'select'
      ? { options: toOptionList(answer.answer) }
      : {}),
  };
}

/**
 * The answer in the shape `PropertyValueLabel` expects for the synthetic
 * property type from {@link buildBookingAnswerPropertyType}: a checkbox is
 * stored as a boolean but the boolean field type resolves `'true'`/`'false'`
 * option keys; a multi-select is an array; everything else passes through
 * (null / undefined / '' render as the standard "—" placeholder).
 */
export function getBookingAnswerDisplayValue(answer: BookingAnswer): unknown {
  if (answer.type === 'checkbox') {
    if (
      answer.answer === null ||
      answer.answer === undefined ||
      answer.answer === ''
    ) {
      return null;
    }
    return answer.answer === true || answer.answer === 'true'
      ? 'true'
      : 'false';
  }
  if (answer.type === 'multi_select') {
    if (Array.isArray(answer.answer)) {
      return answer.answer;
    }
    return answer.answer === null ||
      answer.answer === undefined ||
      answer.answer === ''
      ? null
      : [answer.answer];
  }
  return answer.answer;
}
